import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { parseCsv, cleanCsvField } from '../../utils/csv';
import { canWrite } from '../../utils/roles';

function normalize(str) {
  return str?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') || '';
}

// CSV de Odoo (product.template): "Nombre","Precio de venta","Ventas","Descripción emergente...","Creado por","Costo"
function parseProductsCsv(text) {
  const [, ...dataRows] = parseCsv(text);
  return dataRows.map(([name, price]) => ({
    name: cleanCsvField(name),
    unit_price: price ? Number(price) : null,
  })).filter(r => r.name);
}

function formatPrice(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function resourceOptions(catalog, type) {
  if (type === 'article') return catalog.articles || [];
  if (type === 'rack') return catalog.racks || [];
  return catalog.packs || [];
}

function resourceLabel(resource) {
  const prefix = resource.resource_type === 'article' ? 'Artículo' : resource.resource_type === 'rack' ? 'Rack' : 'Pack';
  return `${prefix}: ${resource.resource_name || 'Sin recurso'}`;
}

function ConfigurationsEditor({ productId }) {
  const [catalog, setCatalog] = useState({ articles: [], racks: [], packs: [] });
  const [configurations, setConfigurations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [catalogResponse, configurationsResponse] = await Promise.all([
        api.get('/products/resources/catalog'),
        api.get(`/products/${productId}/configurations`),
      ]);
      setCatalog(catalogResponse.data || { articles: [], racks: [], packs: [] });
      const nextConfigurations = Array.isArray(configurationsResponse.data) ? configurationsResponse.data : [];
      setConfigurations(nextConfigurations);
      setSelectedId(current => current && nextConfigurations.some(config => config.id === current) ? current : nextConfigurations[0]?.id || null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las configuraciones');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const selected = configurations.find(configuration => configuration.id === selectedId);
    if (!selected) {
      setDraft(null);
      return;
    }
    setDraft({
      ...selected,
      unit_price: selected.unit_price ?? '',
      resources: (selected.resources || []).map(resource => ({ ...resource })),
    });
  }, [configurations, selectedId]);

  const addConfiguration = () => {
    setSelectedId(null);
    setDraft({
      name: '', description: '', unit_price: '', is_default: configurations.length === 0,
      active: true, resources: [],
    });
  };

  const updateDraft = patch => setDraft(current => ({ ...current, ...patch }));

  const addResource = () => {
    setDraft(current => ({
      ...current,
      resources: [...(current.resources || []), {
        resource_type: 'article', article_type_id: '', rack_id: null, access_pack_id: null,
        quantity: 1, note: '', sort_order: current.resources?.length || 0,
      }],
    }));
  };

  const updateResource = (index, patch) => setDraft(current => ({
    ...current,
    resources: current.resources.map((resource, currentIndex) => currentIndex === index ? { ...resource, ...patch } : resource),
  }));

  const removeResource = index => setDraft(current => ({
    ...current,
    resources: current.resources.filter((_, currentIndex) => currentIndex !== index),
  }));

  const save = async () => {
    if (!draft?.name?.trim()) {
      setError('Indicá un nombre para la configuración');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: draft.name,
        description: draft.description,
        unit_price: draft.unit_price,
        is_default: draft.is_default,
        active: draft.active,
        sort_order: draft.sort_order || 0,
      };
      const configurationResponse = draft.id
        ? await api.put(`/products/${productId}/configurations/${draft.id}`, payload)
        : await api.post(`/products/${productId}/configurations`, payload);
      const configuration = configurationResponse.data;
      const resources = (draft.resources || []).map((resource, index) => ({
        resource_type: resource.resource_type,
        article_type_id: resource.resource_type === 'article' ? Number(resource.article_type_id) : null,
        rack_id: resource.resource_type === 'rack' ? Number(resource.rack_id) : null,
        access_pack_id: resource.resource_type === 'pack' ? Number(resource.access_pack_id) : null,
        quantity: Number(resource.quantity) || 1,
        note: resource.note || null,
        sort_order: index,
      }));
      await api.put(`/products/${productId}/configurations/${configuration.id}/resources`, { resources });
      setSelectedId(configuration.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const removeConfiguration = async () => {
    if (!draft?.id || !window.confirm(`¿Eliminar la configuración “${draft.name}”?`)) return;
    setSaving(true);
    try {
      await api.delete(`/products/${productId}/configurations/${draft.id}`);
      setSelectedId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo eliminar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '10px 0', color: 'var(--text-dim)' }}>Cargando configuraciones…</div>;

  return (
    <section style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div>
          <div className="form-label" style={{ margin: 0 }}>Configuraciones del servicio</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>Cada configuración define una receta de inventario independiente.</div>
        </div>
        <button type="button" className="btn btn-ghost" onClick={addConfiguration}>+ Configuración</button>
      </div>

      {error && <div className="alert alert-error" style={{ marginTop: 10 }}>{error}</div>}

      {configurations.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {configurations.map(configuration => (
            <button
              type="button"
              key={configuration.id}
              className={`btn ${selectedId === configuration.id ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setSelectedId(configuration.id)}
            >
              {configuration.name}{configuration.is_default ? ' · Principal' : ''}
            </button>
          ))}
        </div>
      )}

      {!draft ? (
        <div style={{ marginTop: 14, padding: 14, border: '1px dashed var(--border)', color: 'var(--text-dim)', fontSize: 13 }}>
          Este servicio todavía no tiene configuraciones. Creá una para definir su equipo esperado.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 140px', gap: 10 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nombre de la configuración</label>
              <input className="form-input" value={draft.name} onChange={event => updateDraft({ name: event.target.value })} placeholder="Ej: Exterior, estándar, sin estructura…" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Precio propio</label>
              <input className="form-input" type="number" min="0" step="any" value={draft.unit_price} onChange={event => updateDraft({ unit_price: event.target.value })} placeholder="Usa el del servicio" />
            </div>
          </div>
          <textarea className="form-input" value={draft.description || ''} onChange={event => updateDraft({ description: event.target.value })} rows={2} placeholder="Detalle comercial opcional…" style={{ resize: 'vertical' }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!draft.is_default} onChange={event => updateDraft({ is_default: event.target.checked })} />
            Configuración principal al cotizar
          </label>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span className="form-label" style={{ margin: 0 }}>Receta de inventario</span>
              <button type="button" className="btn btn-ghost" onClick={addResource}>+ Recurso</button>
            </div>
            {(draft.resources || []).length === 0 ? (
              <div style={{ color: 'var(--text-dim)', fontSize: 13 }}>Sin recursos predefinidos. Podrás agregar equipo manualmente en la cotización.</div>
            ) : draft.resources.map((resource, index) => {
              const options = resourceOptions(catalog, resource.resource_type);
              const selectedValue = resource.resource_type === 'article' ? resource.article_type_id : resource.resource_type === 'rack' ? resource.rack_id : resource.access_pack_id;
              return (
                <div key={resource.id || `new-${index}`} style={{ display: 'grid', gridTemplateColumns: '112px minmax(0, 1fr) 76px 28px', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <select className="form-input" value={resource.resource_type} onChange={event => updateResource(index, {
                    resource_type: event.target.value, article_type_id: '', rack_id: null, access_pack_id: null,
                  })}>
                    <option value="article">Artículo</option>
                    <option value="rack">Rack</option>
                    <option value="pack">Pack</option>
                  </select>
                  <select className="form-input" value={selectedValue || ''} onChange={event => {
                    const id = event.target.value || '';
                    updateResource(index, resource.resource_type === 'article' ? { article_type_id: id } : resource.resource_type === 'rack' ? { rack_id: id } : { access_pack_id: id });
                  }}>
                    <option value="">Seleccionar…</option>
                    {options.map(option => <option key={option.id} value={option.id}>{option.code ? `${option.code} · ` : ''}{option.name}</option>)}
                  </select>
                  <input className="form-input" type="number" min="0.01" step="any" value={resource.quantity} onChange={event => updateResource(index, { quantity: event.target.value })} title="Cantidad" />
                  <button type="button" className="btn btn-icon btn-ghost" onClick={() => removeResource(index)} title={`Quitar ${resourceLabel(resource)}`}>✕</button>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {draft.id && <button type="button" className="btn btn-ghost" onClick={removeConfiguration} disabled={saving}>Eliminar</button>}
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar configuración'}</button>
          </div>
        </div>
      )}
    </section>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || '',
    unit: product?.unit || 'unidad',
    unit_price: product?.unit_price != null ? String(product.unit_price) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es requerido'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        unit_price: form.unit_price !== '' ? Number(form.unit_price) : null,
      };
      const { data } = product
        ? await api.put(`/products/${product.id}`, payload)
        : await api.post('/products', payload);
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{product ? 'Editar servicio comercial' : 'Nuevo servicio comercial'}</h3>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nombre *</label>
              <input className="form-input" value={form.name} onChange={set('name')} placeholder="Ej: Pantalla LED 4x3" autoFocus spellCheck={false} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Categoría</label>
              <input className="form-input" value={form.category} onChange={set('category')} placeholder="Ej: Sonido, Iluminación, Servicio…" spellCheck={false} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Descripción</label>
              <textarea className="form-input" value={form.description} onChange={set('description')} placeholder="Descripción opcional…" rows={2} style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Unidad</label>
                <input className="form-input" value={form.unit} onChange={set('unit')} placeholder="unidad, hora, día…" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Precio unitario</label>
                <input className="form-input" type="number" min="0" step="any" value={form.unit_price} onChange={set('unit_price')} placeholder="0" />
              </div>
            </div>
            {product && <ConfigurationsEditor productId={product.id} />}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : product ? 'Guardar cambios' : 'Crear servicio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'ventas.productos');

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const fileInputRef = useRef(null);

  const loadSyncStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/settings/products_last_sync');
      setSyncInfo(data.value ? JSON.parse(data.value) : null);
    } catch { /* informativo, no debe romper la lista */ }
  }, []);
  useEffect(() => {
    loadSyncStatus();
    const t = setInterval(loadSyncStatus, 60000);
    return () => clearInterval(t);
  }, [loadSyncStatus]);

  const handleSyncOdoo = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post('/products/sync-odoo');
      alert(`Sincronizado con Odoo · ${data.fetched} productos (nuevos: ${data.inserted}, actualizados: ${data.updated}, archivados: ${data.archived})`);
      await load();
      await loadSyncStatus();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo sincronizar con Odoo');
    } finally {
      setSyncing(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products${showInactive ? '?all=1' : ''}`);
      setProducts(Array.isArray(data) ? data : []);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = products.filter(p => {
    const q = normalize(search);
    return !q || normalize(p.name).includes(q) || normalize(p.category).includes(q) || normalize(p.description).includes(q);
  });

  // Group by category
  const byCategory = filtered.reduce((acc, p) => {
    const cat = p.category || 'Sin categoría';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const handleSaved = saved => {
    setProducts(prev => {
      const idx = prev.findIndex(p => p.id === saved.id);
      return idx >= 0 ? prev.map((p, i) => i === idx ? saved : p) : [...prev, saved];
    });
    setShowModal(false);
    setEditing(null);
  };

  const handleToggle = async product => {
    try {
      const { data } = await api.put(`/products/${product.id}`, { active: !product.active });
      handleSaved(data);
    } catch {}
  };

  const handleImportFile = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const rows = parseProductsCsv(String(reader.result));
        if (rows.length === 0) { alert('No se encontraron filas válidas en el archivo'); return; }
        const { data } = await api.post('/products/import', { rows });
        alert(`Importados: ${data.imported} · Ya existían: ${data.skipped} · Total en archivo: ${data.total}`);
        load();
      } catch (err) {
        alert(err.response?.data?.error || 'Error al importar el archivo');
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Servicios comerciales</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {syncInfo?.at && (() => {
            const min = Math.max(0, Math.round((Date.now() - new Date(syncInfo.at).getTime()) / 60000));
            const txt = min < 1 ? 'recién' : min < 60 ? `hace ${min} min` : min < 1440 ? `hace ${Math.round(min / 60)} h` : `hace ${Math.round(min / 1440)} d`;
            return (
              <span
                title={syncInfo.error || (syncInfo.count ? `${syncInfo.count} productos de Odoo` : '')}
                style={{ fontSize: 12, color: syncInfo.ok ? 'var(--text-dim)' : 'var(--red, #ef4444)' }}
              >
                {syncInfo.ok ? `Odoo · actualizado ${txt}` : `Odoo · error ${txt}`}
              </span>
            );
          })()}
          {isAdmin && (
            <>
              <button className="btn btn-ghost" onClick={handleSyncOdoo} disabled={syncing}>
                {syncing ? 'Sincronizando…' : 'Sincronizar con Odoo'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
              <button className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                {importing ? 'Importando…' : 'Importar CSV'}
              </button>
              <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
                + Nuevo servicio
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
          placeholder="Buscar por nombre o categoría…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Mostrar inactivos
          </label>
        )}
        <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          {filtered.length} {filtered.length === 1 ? 'servicio' : 'servicios'}
        </span>
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-dim)' }}>
          {search ? 'Sin resultados.' : 'No hay servicios registrados.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(byCategory).sort(([a], [b]) => a.localeCompare(b, 'es')).map(([cat, items]) => (
            <div key={cat}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                {cat}
              </div>
              <div className="vlist">
                {items.map(p => (
                  <div key={p.id} className={`vlist-row${p.active ? '' : ' inactive'}`}>
                    <span className="vlist-name">{p.name}</span>
                    <span className="vlist-meta">{p.description || ''}</span>
                    <span className="vlist-right">
                      {p.unit_price != null ? (
                        <>
                          <span style={{ fontWeight: 600, color: 'var(--text)' }}>${formatPrice(p.unit_price)}</span>
                          <span style={{ fontSize: 11, marginLeft: 3 }}>/ {p.unit}</span>
                        </>
                      ) : p.unit}
                    </span>
                    {isAdmin && (
                      <div className="vlist-actions">
                        <button className="btn btn-icon btn-ghost" title="Editar" onClick={() => { setEditing(p); setShowModal(true); }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button className="btn btn-icon btn-ghost" title={p.active ? 'Desactivar' : 'Activar'} onClick={() => handleToggle(p)}>
                          {p.active ? (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                          ) : (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ProductModal
          product={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
